import styled from '@emotion/styled';
import { AuthorizationStates } from '@racemap/utilities/consts/events';
import { formatEuroPrice } from '@racemap/utilities/functions/billing/formatEuroPrice';
import { isGroupEvent } from '@racemap/utilities/functions/utils';
import { Popover } from 'antd';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { useIsNarrowWindow, useIsTinyWindow } from 'src/lib/customHooks';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useEventBasePrice } from 'src/lib/customHooks/useEventBasePrice';
import type { CurrentEvent } from '../../../store/events/events_reducers';

interface Props {
  event: Immutable<CurrentEvent>;
  /** Renders as one line of metadata rather than a block of its own. */
  inline?: boolean;
}

export const PriceField: FC<Props> = ({ event, inline = false }) => {
  const user = useCurrentUser();
  const eventBasePrice = useEventBasePrice(event.type);
  const isNarrowWindow = useIsNarrowWindow();
  const isTinyWindow = useIsTinyWindow();

  if (isGroupEvent(event)) return null;
  if (user == null || user.id !== event.creatorId || eventBasePrice == null) return <></>;
  if (event.authorization !== AuthorizationStates.NONE) return <></>;
  // Block, badges and buttons do not fit one row below 768px.
  if (inline !== isNarrowWindow) return null;
  // No space on a phone; the payment dialog states the price.
  if (inline && isTinyWindow) return null;

  if (inline) return <span>{formatEuroPrice(eventBasePrice.amount)}</span>;

  return (
    <Popover
      title={'This price does not include VAT'}
      content={
        'Please update your account with the correct country and/or VAT ID in order to change the tax rate.'
      }
    >
      <Container>
        <Label>Base Price</Label>
        <Value>
          {formatEuroPrice(eventBasePrice.amount)} <Vat>excl. VAT</Vat>
        </Value>
      </Container>
    </Popover>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  flex: none;
  padding-right: 4px;
`;

const Label = styled.span`
  font-size: 11px;
  line-height: 14px;
  color: #8c8c8c;
`;

const Value = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 19px;
  white-space: nowrap;
`;

const Vat = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: #8c8c8c;
`;
